import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { isLikelyImageFile } from './image-file';
import { translateFirebaseErrorObject } from '../utils/firebaseErrors';
import { app, auth } from './firebase';

const MAX_BYTES = 5 * 1024 * 1024;
const MAX_EDGE = 1280;

type StorageFolder = 'misuse-reports' | 'profile-photos' | 'support-reports';

function blobFromCanvas(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Não foi possível processar a foto.'));
      },
      'image/jpeg',
      0.82,
    );
  });
}

async function compressImage(file: File): Promise<Blob> {
  if (!isLikelyImageFile(file)) {
    throw new Error('Envie um arquivo de imagem.');
  }
  if (file.size > MAX_BYTES) {
    throw new Error('A foto deve ter no máximo 5 MB.');
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('Não foi possível ler esta foto. Tente outra em JPEG ou PNG.');
  }
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    throw new Error('Não foi possível processar a foto.');
  }
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  return blobFromCanvas(canvas);
}

export async function uploadMisuseReportPhoto(file: File): Promise<string> {
  return uploadUserImage(file, 'misuse-reports');
}

export async function uploadProfilePhoto(file: File): Promise<string> {
  return uploadUserImage(file, 'profile-photos');
}

export async function uploadSupportReportPhoto(file: File): Promise<string> {
  return uploadUserImage(file, 'support-reports');
}

async function uploadUserImage(file: File, folder: StorageFolder): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Faça login para enviar a foto.');
  }
  if (!import.meta.env.VITE_FIREBASE_STORAGE_BUCKET) {
    throw new Error('Upload de foto não está configurado neste ambiente.');
  }

  const blob = await compressImage(file);
  const storage = getStorage(app);
  const path = `${folder}/${user.uid}/${crypto.randomUUID()}.jpg`;
  const fileRef = ref(storage, path);
  try {
    await uploadBytes(fileRef, blob, { contentType: 'image/jpeg' });
    return await getDownloadURL(fileRef);
  } catch (error) {
    throw new Error(translateFirebaseErrorObject(error));
  }
}
